import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { MEMBERSHIP_PLANS } from '../../../constants/membershipPlans';
import { calcularMesesEntreFechas, formatearPrecio, formatearFecha } from '../../../utils/membresiasUtils';

/**
 * Componente para calcular y mostrar comisiones de agentes
 * 
 * Lógica de comisiones:
 * - Porcentaje de comisión por membresía vendida (configurable, default 15%)
 * - Se calcula sobre el precio pagado después del descuento del 30%
 * - Se calcula por cada mes que la empresa esté activa
 * - Si la empresa cancela y vuelve después, pierde el descuento del 30%
 */
export default function ComisionesAgentes() {
  const [agentes, setAgentes] = useState([]);
  const [agenteSeleccionado, setAgenteSeleccionado] = useState(null);
  const [comisionesData, setComisionesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculando, setCalculando] = useState(false);
  const [porcentajeComision, setPorcentajeComision] = useState(15); // 15% default
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  useEffect(() => {
    cargarAgentes();
  }, []);

  const cargarAgentes = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'agentes'),
        where('activo', '==', true),
        orderBy('nombre', 'asc')
      );
      const snapshot = await getDocs(q);
      const agentesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAgentes(agentesData);
    } catch (error) {
      console.error('Error cargando agentes:', error);
      // Fallback sin orderBy
      try {
        const q = query(collection(db, 'agentes'), where('activo', '==', true));
        const snapshot = await getDocs(q);
        const agentesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        setAgentes(agentesData);
      } catch (error2) {
        console.error('Error en carga alternativa:', error2);
      }
    } finally {
      setLoading(false);
    }
  };

  const calcularComisiones = async (agenteId) => {
    try {
      setCalculando(true);
      const agente = agentes.find(a => a.id === agenteId);
      if (!agente) return;

      // Buscar empresas asignadas al agente
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('agenteAsignado', '==', agenteId)
      );
      const empresasSnapshot = await getDocs(empresasQuery);
      const empresas = empresasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Buscar membresías vendidas por este agente
      // Asumimos que las membresías tienen un campo 'vendidaPorAgente' o similar
      const membresiasQuery = query(
        collection(db, 'membresias_empresas'),
        where('agenteId', '==', agenteId)
      );

      let membresias = [];
      try {
        const membresiasSnapshot = await getDocs(membresiasQuery);
        membresias = membresiasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.warn('Colección membresias_empresas no encontrada, buscando en empresas:', error);
      }

      // Calcular comisiones
      const resultado = await calcularComisionesAgente(
        agente,
        empresas,
        membresias,
        porcentajeComision,
        fechaInicio,
        fechaFin
      );

      setComisionesData(resultado);
      setAgenteSeleccionado(agente);
    } catch (error) {
      console.error('Error calculando comisiones:', error);
      alert('Error al calcular comisiones: ' + error.message);
    } finally {
      setCalculando(false);
    }
  };

  const calcularComisionesAgente = async (agente, empresas, membresias, porcentajeComision, fechaInicio, fechaFin) => {
    const resultado = {
      agente: agente,
      resumen: {
        totalEmpresasVisitadas: empresas.length,
        totalMembresiasVendidas: 0,
        totalMembresiasActivas: 0,
        totalComisionBruta: 0,
        totalComisionNeta: 0,
        totalMesesFacturados: 0
      },
      detalleMembresias: [],
      empresasVisitadas: empresas.map(e => ({
        id: e.id,
        nombre: e.nombre,
        fechaAsignacion: e.fechaAsignacion?.toDate?.() || e.fechaAsignacion || null,
        fechaValidacion: e.fecha_visita_terreno?.toDate?.() || e.fecha_visita_terreno || null,
        estado: e.estado
      }))
    };

    // Filtrar por fechas si se especificaron
    const fechaInicioDate = fechaInicio ? new Date(fechaInicio) : null;
    const fechaFinDate = fechaFin ? new Date(fechaFin + 'T23:59:59') : null;

    // Procesar cada membresía
    for (const membresia of membresias) {
      // Verificar si está en el rango de fechas
      const fechaCreacion = membresia.fechaCreacion?.toDate?.() || membresia.fechaCreacion || new Date();
      if (fechaInicioDate && fechaCreacion < fechaInicioDate) continue;
      if (fechaFinDate && fechaCreacion > fechaFinDate) continue;

      const planId = membresia.planId || membresia.plan || 'premium';
      const plan = MEMBERSHIP_PLANS[planId.toUpperCase()] || MEMBERSHIP_PLANS.PREMIUM;
      
      // Precio base del plan
      let precioBase = plan.price || 0;
      
      // Si fue ofrecida por agente, aplicar descuento del 30%
      const tieneDescuentoAgente = membresia.ofrecidaPorAgente === true || 
                                   membresia.descuentoAgente === true ||
                                   membresia.agenteId === agente.id;
      
      // Si canceló y volvió, perder el descuento
      const perdióDescuento = membresia.canceloYVolvio === true;
      
      let precioPagado = precioBase;
      if (tieneDescuentoAgente && !perdióDescuento) {
        precioPagado = precioBase * 0.7; // 30% de descuento
      }

      // Calcular meses facturados
      let mesesFacturados = 0;
      const fechaInicioMembresia = membresia.fechaInicio?.toDate?.() || membresia.fechaInicio || new Date();
      const fechaFinMembresia = membresia.fechaFin?.toDate?.() || membresia.fechaFin || null;
      const fechaCancelacion = membresia.fechaCancelacion?.toDate?.() || membresia.fechaCancelacion || null;
      const estaActiva = membresia.estado === 'activa' || membresia.estado === 'active';

      if (estaActiva) {
        // Si está activa, calcular hasta hoy o fecha fin
        const fechaHasta = fechaFinDate || fechaFinMembresia || new Date();
        mesesFacturados = calcularMesesEntreFechas(fechaInicioMembresia, fechaHasta);
      } else if (fechaCancelacion) {
        // Si canceló, calcular hasta la fecha de cancelación
        mesesFacturados = calcularMesesEntreFechas(fechaInicioMembresia, fechaCancelacion);
      } else if (fechaFinMembresia) {
        mesesFacturados = calcularMesesEntreFechas(fechaInicioMembresia, fechaFinMembresia);
      }

      // Calcular comisión (porcentaje sobre precio pagado por mes)
      const comisionPorMes = precioPagado * (porcentajeComision / 100);
      const comisionTotal = comisionPorMes * mesesFacturados;

      // Buscar empresa asociada
      const empresaId = membresia.empresaId || membresia.empresa;
      let empresa = null;
      if (empresaId) {
        try {
          const empresaDoc = await getDoc(doc(db, 'empresas', empresaId));
          if (empresaDoc.exists()) {
            empresa = { id: empresaDoc.id, nombre: empresaDoc.data().nombre };
          }
        } catch (error) {
          console.warn('Error buscando empresa:', error);
        }
      }

      resultado.detalleMembresias.push({
        membresiaId: membresia.id,
        empresaId: empresaId,
        empresaNombre: empresa?.nombre || 'Empresa no encontrada',
        planId: planId,
        planNombre: plan.name,
        precioBase: precioBase,
        precioPagado: precioPagado,
        tieneDescuentoAgente: tieneDescuentoAgente && !perdióDescuento,
        perdióDescuento: perdióDescuento,
        fechaInicio: fechaInicioMembresia,
        fechaFin: fechaFinMembresia,
        fechaCancelacion: fechaCancelacion,
        estado: membresia.estado || 'desconocido',
        mesesFacturados: mesesFacturados,
        comisionPorMes: comisionPorMes,
        comisionTotal: comisionTotal,
        fechaCreacion: fechaCreacion
      });

      resultado.resumen.totalMembresiasVendidas++;
      if (estaActiva) resultado.resumen.totalMembresiasActivas++;
      resultado.resumen.totalMesesFacturados += mesesFacturados;
      resultado.resumen.totalComisionBruta += comisionTotal;
    }

    // Comisión neta (después de impuestos si aplica)
    resultado.resumen.totalComisionNeta = resultado.resumen.totalComisionBruta * 0.9; // 90% neto (asumiendo 10% de retención)

    return resultado;
  };


  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Cargando agentes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          💰 Cálculo de Comisiones de Agentes
        </h1>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Porcentaje de Comisión (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={porcentajeComision}
              onChange={(e) => setPorcentajeComision(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFechaInicio('');
                setFechaFin('');
              }}
              className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Limpiar Fechas
            </button>
          </div>
        </div>

        {/* Selector de Agente */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Agente
          </label>
          <div className="flex gap-2">
            <select
              value={agenteSeleccionado?.id || ''}
              onChange={(e) => {
                const agenteId = e.target.value;
                if (agenteId) {
                  calcularComisiones(agenteId);
                } else {
                  setAgenteSeleccionado(null);
                  setComisionesData(null);
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Seleccionar Agente --</option>
              {agentes.map(agente => (
                <option key={agente.id} value={agente.id}>
                  {agente.nombre} {agente.zona_asignada ? `(${agente.zona_asignada.replace(/_/g, ' ')})` : ''}
                </option>
              ))}
            </select>
            {calculando && (
              <div className="flex items-center px-4">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resultados */}
      {comisionesData && (
        <div className="space-y-6">
          {/* Resumen */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-4">
              Resumen de Comisiones - {comisionesData.agente.nombre}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm opacity-90">Empresas Visitadas</div>
                <div className="text-2xl font-bold">{comisionesData.resumen.totalEmpresasVisitadas}</div>
              </div>
              <div>
                <div className="text-sm opacity-90">Membresías Vendidas</div>
                <div className="text-2xl font-bold">{comisionesData.resumen.totalMembresiasVendidas}</div>
              </div>
              <div>
                <div className="text-sm opacity-90">Meses Facturados</div>
                <div className="text-2xl font-bold">{comisionesData.resumen.totalMesesFacturados}</div>
              </div>
              <div>
                <div className="text-sm opacity-90">Comisión Total</div>
                <div className="text-2xl font-bold">{formatearPrecio(comisionesData.resumen.totalComisionBruta)}</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex justify-between items-center">
                <span className="text-lg">Comisión Neta (90%):</span>
                <span className="text-2xl font-bold">{formatearPrecio(comisionesData.resumen.totalComisionNeta)}</span>
              </div>
            </div>
          </div>

          {/* Detalle de Membresías */}
          {comisionesData.detalleMembresias.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Detalle de Membresías Vendidas
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Base</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Pagado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descuento 30%</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meses</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comisión/Mes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comisión Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comisionesData.detalleMembresias.map((membresia, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{membresia.empresaNombre}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{membresia.planNombre}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatearPrecio(membresia.precioBase)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatearPrecio(membresia.precioPagado)}</td>
                        <td className="px-4 py-3 text-sm">
                          {membresia.tieneDescuentoAgente ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ Sí
                            </span>
                          ) : membresia.perdióDescuento ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ✗ Perdido
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{membresia.mesesFacturados}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatearPrecio(membresia.comisionPorMes)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-blue-600">{formatearPrecio(membresia.comisionTotal)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            membresia.estado === 'activa' || membresia.estado === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {membresia.estado}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="7" className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Total:</td>
                      <td className="px-4 py-3 text-sm font-bold text-blue-600">
                        {formatearPrecio(comisionesData.resumen.totalComisionBruta)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Empresas Visitadas */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Empresas Visitadas ({comisionesData.empresasVisitadas.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Asignación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Validación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {comisionesData.empresasVisitadas.map((empresa) => (
                    <tr key={empresa.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{empresa.nombre}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatearFecha(empresa.fechaAsignacion)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatearFecha(empresa.fechaValidacion)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          empresa.estado === 'validada' || empresa.estado === 'activa'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {empresa.estado || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          📋 Información sobre el Sistema de Comisiones
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• <strong>Descuento del 30%:</strong> Solo aplica a membresías ofrecidas directamente por el agente</li>
          <li>• <strong>Período de prueba:</strong> Los agentes pueden ofrecer X días gratis, después de los cuales la empresa debe cancelar si no quiere continuar</li>
          <li>• <strong>Cancelación y reactivación:</strong> Si una empresa cancela y vuelve después, pierde el descuento del 30% y debe pagar el precio completo</li>
          <li>• <strong>Cálculo de comisiones:</strong> Se calcula sobre el precio pagado (después del descuento) por cada mes facturado</li>
          <li>• <strong>Comisión neta:</strong> Se aplica una retención del 10% para impuestos y otros conceptos</li>
        </ul>
      </div>
    </div>
  );
}

